import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { createProductColumns } from './ProductTableColumns';
import ProductPagination from '../../ProductPagination';

const ProductTable = ({ 
  products, 
  pagination, 
  onPageChange, 
  onProductClick,
  selectionHandlers,
  onDeleteProduct
}) => {
  // React Table 컬럼 정의
  const columns = React.useMemo(() => {
    return createProductColumns(selectionHandlers, onDeleteProduct);
  }, [selectionHandlers, onDeleteProduct]);

  // React Table 설정
  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true, // 서버 사이드 페이지네이션 사용
    pageCount: pagination.pages,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    state: {
      pagination: {
        pageIndex: pagination.page - 1, // API는 1부터 시작, React Table은 0부터 시작
        pageSize: pagination.size,
      },
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' 
        ? updater({ pageIndex: pagination.page - 1, pageSize: pagination.size })
        : updater;
      
      // 페이지 변경 시 API 호출
      onPageChange(newPagination.pageIndex + 1); // API는 1부터 시작
    },
  });

  // 사이트별 행 배경색 설정
  const getRowBackgroundColor = (isPublished) => {
    if (isPublished) {
      return 'bg-yellow-50 hover:bg-yellow-200';
    } else {
      return 'bg-gray-100 hover:bg-gray-300';
    }
  };

  return (
    <div className="mt-5 hidden md:block overflow-x-auto">
      <table className="w-full border-collapse table-fixed min-w-full">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th 
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  className={`bg-gray-50 px-3 py-3 text-left border-b-2 border-gray-200 text-gray-800 text-sm cursor-pointer hover:bg-gray-100 ${header.column.columnDef.meta?.className || ''}`}
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {{
                    asc: ' 🔼',
                    desc: ' 🔽',
                  }[header.column.getIsSorted()] ?? null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-10 text-gray-500">
                제품 데이터가 없습니다.
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr 
                key={row.id}
                onClick={(e) => {
                  // 체크박스나 버튼 클릭 시에는 상세 페이지로 이동하지 않음
                  if (e.target.type === 'checkbox' || e.target.tagName === 'BUTTON') {
                    return;
                  }
                  onProductClick(row.original.id);
                }}
                className={`cursor-pointer transition-colors ${getRowBackgroundColor(row.original.published_to_shopify)}`}
              >
                {row.getVisibleCells().map(cell => (
                  <td 
                    key={cell.id} 
                    className={`px-3 py-2 border-b border-gray-200 text-sm ${cell.column.columnDef.meta?.className || ''}`}
                    style={{ width: cell.column.getSize() }}
                    onClick={(e) => {
                      // 체크박스 셀 클릭 시에는 상세 페이지로 이동하지 않음
                      if (cell.column.id === 'select') {
                        e.stopPropagation();
                      }
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* 페이지네이션 */}
      <ProductPagination
        pagination={pagination}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default ProductTable;
